import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import api, { extractData, handleApiError } from '../../utils/api';

interface EditorConfig {
  config: any;
  onlyofficeUrl: string;
}

const DocumentEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorConfig, setEditorConfig] = useState<EditorConfig | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const docEditorRef = useRef<any>(null);

  useEffect(() => {
    if (!id) return;

    const fetchConfig = async () => {
      try {
        const res = await api.get(`/documents/${id}/editor`);
        const data = extractData<EditorConfig>(res);
        setEditorConfig(data);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [id]);

  useEffect(() => {
    if (!editorConfig) return;

    // Load OnlyOffice API script
    const scriptId = 'onlyoffice-api-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    const initEditor = () => {
      if (docEditorRef.current) {
        docEditorRef.current.destroyEditor?.();
      }

      // @ts-ignore
      if (window.DocsAPI) {
        // @ts-ignore
        docEditorRef.current = new window.DocsAPI.DocEditor('onlyoffice-editor', {
          ...editorConfig.config,
          width: '100%',
          height: '100%',
          events: {
            onAppReady: () => {
              console.log('[OO] App ready');
            },
            onError: (event: any) => {
              console.error('[OO] onError:', JSON.stringify(event?.data || event));
            },
            onWarning: (event: any) => {
              console.warn('[OO] onWarning:', JSON.stringify(event?.data || event));
            },
            onDocumentStateChange: (event: any) => {
              console.log('[OO] DocumentStateChange:', JSON.stringify(event?.data || event));
            },
            onRequestSaveAs: (event: any) => {
              console.log('[OO] onRequestSaveAs', JSON.stringify(event?.data || event));
            },
            onOutdatedVersion: (event: any) => {
              console.warn('[OO] outdated version', JSON.stringify(event?.data || event));
            },
            onDownloadAs: (event: any) => {
              console.log('[OO] onDownloadAs', JSON.stringify(event?.data || event));
            },
            onCollaborativeChanges: () => {
              console.log('[OO] collaborative changes');
            },
            onRequestClose: () => {
              console.log('[OO] request close');
              navigate(-1);
            },
            onMetaChange: (event: any) => {
              console.log('[OO] meta change', JSON.stringify(event?.data || event));
            },
          },
        });
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `${editorConfig.onlyofficeUrl}/web-apps/apps/api/documents/api.js`;
      script.async = true;
      script.onload = initEditor;
      script.onerror = () => setError('Failed to load OnlyOffice editor');
      document.head.appendChild(script);
    } else {
      // Script already loaded
      initEditor();
    }

    return () => {
      if (docEditorRef.current) {
        docEditorRef.current.destroyEditor?.();
        docEditorRef.current = null;
      }
    };
  }, [editorConfig]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error" variant="h6" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography
          component="a"
          href="#"
          onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(-1); }}
          sx={{ cursor: 'pointer', color: 'primary.main' }}
        >
          Go back
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Minimal header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          height: 40,
        }}
      >
        <Tooltip title="Back">
          <IconButton size="small" onClick={() => navigate(-1)}>
            <BackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="body2" fontWeight={500} sx={{ ml: 1 }}>
          {editorConfig?.config?.document?.title || 'Document'}
        </Typography>
      </Box>

      {/* OnlyOffice Editor */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <div id="onlyoffice-editor" ref={editorRef} style={{ width: '100%', height: '100%' }} />
      </Box>
    </Box>
  );
};

export default DocumentEditor;
